// Pure, game-agnostic layout geometry + viewport classification. This module
// imports NO rendering engine and performs NO DOM access, so every value here
// is unit-testable. Game-specific design boxes/regions live in each game; this
// module provides the shared primitives (viewport, insets, rects, fit, mode
// classification, composition kind, dpr cap, reduced-effects, touch targets).
export const NO_INSETS = { top: 0, right: 0, bottom: 0, left: 0 };
/**
 * Contain-fit a design box (designW x designH) into a target rect, centered.
 * Preserves aspect ratio. Generic so a scene can place any region container
 * into its allocated screen rect.
 */
export function fitRect(designW, designH, rect) {
    const dw = Math.max(1, designW);
    const dh = Math.max(1, designH);
    const scale = Math.min(rect.w / dw, rect.h / dh);
    return {
        scale,
        x: rect.x + (rect.w - dw * scale) / 2,
        y: rect.y + (rect.h - dh * scale) / 2,
    };
}
/** The visible safe-area rect in renderer coordinates: viewport minus insets. */
export function safeRect(vp, insets) {
    const x = Math.max(0, insets.left);
    const y = Math.max(0, insets.top);
    const w = Math.max(1, vp.width - insets.left - insets.right);
    const h = Math.max(1, vp.height - insets.top - insets.bottom);
    return { x, y, w, h };
}
/** Largest viewport min-edge still treated as a phone (e.g. 390/430). */
export const PHONE_MAX_MIN_SIDE = 500;
/** Smallest max-edge that forces desktop when paired with a fine pointer. */
export const DESKTOP_MIN_MAX_SIDE = 1200;
/**
 * Classifies the viewport into one of five layout modes using width, height,
 * orientation, overall size, and — as a refinement, not the sole signal —
 * coarse-pointer capability. Deliberately NOT user-agent based.
 */
export function classifyLayout(vp) {
    const w = Math.max(1, vp.width);
    const h = Math.max(1, vp.height);
    const portrait = h > w;
    const minSide = Math.min(w, h);
    const maxSide = Math.max(w, h);
    const coarse = vp.coarsePointer ?? false;
    if (maxSide >= DESKTOP_MIN_MAX_SIDE) {
        if (!coarse)
            return 'desktop';
        return portrait ? 'tablet-portrait' : 'tablet-landscape';
    }
    if (minSide <= PHONE_MAX_MIN_SIDE) {
        return portrait ? 'mobile-portrait' : 'mobile-landscape';
    }
    return portrait ? 'tablet-portrait' : 'tablet-landscape';
}
/** Orientation implied by a mode. */
export function orientationOf(mode) {
    return mode === 'mobile-portrait' || mode === 'tablet-portrait' ? 'portrait' : 'landscape';
}
/**
 * Which composition a mode renders with:
 *   unified  -> a single fixed virtual stage contain-fit (desktop/tablet-landscape)
 *   portrait -> compact full-screen reflow stacked top->bottom (portrait touch)
 *   rail     -> landscape phones: main area + a vertical control rail
 */
export function compositionKind(mode) {
    if (mode === 'mobile-portrait' || mode === 'tablet-portrait')
        return 'portrait';
    if (mode === 'mobile-landscape')
        return 'rail';
    return 'unified';
}
/** True for modes that use a bespoke mobile composition (portrait or rail). */
export function isMobileComposition(mode) {
    return compositionKind(mode) !== 'unified';
}
/** True for the small touch phones that should drop effects + cap resolution. */
export function isMobile(mode) {
    return mode === 'mobile-portrait' || mode === 'mobile-landscape';
}
/**
 * Device-pixel-ratio cap. Phones are capped to ~1.5 to save fill-rate on weaker
 * GPUs; tablets/desktops keep 2. Always clamped to the device's real dpr.
 */
export function dprCap(mode, deviceDpr = 1) {
    const cap = isMobile(mode) ? 1.5 : 2;
    return Math.min(Math.max(1, deviceDpr), cap);
}
/** Whether to reduce blur/glow/particle work for this mode (phones). */
export function reducedEffects(mode) {
    return isMobile(mode);
}
/** Minimum interactive target size in CSS pixels (WCAG/Apple guidance). */
export const MIN_TOUCH_TARGET_PX = 44;
/**
 * Converts a renderer-space length to CSS pixels. A scene that renders its
 * design boxes at `scale` can check that interactive elements are at least
 * MIN_TOUCH_TARGET_PX CSS px: cssPx(designSize * scale, dpr).
 */
export function rendererToCssPx(rendererPx, dpr = 1) {
    return rendererPx / Math.max(1, dpr);
}
//# sourceMappingURL=layout.js.map